<template>
  <div class="header">
    <div
      class="brand-stack"
      @mouseenter="showQuickMenu"
      @mouseleave="hideQuickMenu"
    >
      <div class="logo">stArt</div>
      <div v-if="isQuickMenuOpen" class="quick-menu">
        <div class="quick-menu-row">
          <span>Notes</span>
          <label class="switch">
            <input
              type="checkbox"
              :checked="dashboardStore.isNotesVisible"
              @change="dashboardStore.toggleNotesVisible()"
            />
            <span class="slider"></span>
          </label>
        </div>
        <div class="quick-menu-row">
          <span>Tasks</span>
          <label class="switch">
            <input
              type="checkbox"
              :checked="dashboardStore.isTasksVisible"
              @change="dashboardStore.toggleTasksVisible()"
            />
            <span class="slider"></span>
          </label>
        </div>
        <div class="quick-menu-row">
          <span>Schedule</span>
          <label class="switch">
            <input
              type="checkbox"
              :checked="dashboardStore.isSchedulesVisible"
              @change="dashboardStore.toggleSchedulesVisible()"
            />
            <span class="slider"></span>
          </label>
        </div>
        <div class="quick-menu-row">
          <span>Calendar</span>
          <label class="switch">
            <input
              type="checkbox"
              :checked="dashboardStore.isCalendarVisible"
              @change="dashboardStore.toggleCalendarVisible()"
            />
            <span class="slider"></span>
          </label>
        </div>
      </div>
    </div>
    <div class="header-tools">
      <!-- UBoard Trigger -->
      <div class="uboard-trigger-wrap">
        <a
          class="uboard-trigger"
          href="https://uboard.info/uec"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="uboard を開く"
        >
          uBoard
        </a>
        <aside class="uboard-sidebar" aria-label="UEC Dashboard サイドバー">
          <div class="uboard-sidebar-header">
            <span>UEC Dashboard</span>
            <a
              href="https://uboard.info/uec"
              target="_blank"
              rel="noopener noreferrer"
              >別タブ</a
            >
          </div>
          <iframe
            title="UEC Dashboard Sidebar"
            loading="lazy"
            src="about:blank"
            data-src="https://uboard.info/uec"
            referrerpolicy="strict-origin-when-cross-origin"
            @mouseenter="loadUboardFrame"
            @focusin="loadUboardFrame"
          ></iframe>
        </aside>
      </div>

      <!-- Gmail Launcher -->
      <div class="gmail-launcher" aria-label="Gmail を開く">
        <button
          type="button"
          class="gmail-launcher-button"
          aria-label="Gmail を開く"
          @click="dashboardStore.toggleGmailSidebar()"
        >
          <i class="fas fa-envelope"></i>
        </button>
      </div>

      <!-- Google Services -->
      <GoogleServices />
    </div>

    <!-- Gmail Sidebar -->
    <GmailSidebar v-if="dashboardStore.isGmailSidebarOpen" />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useDashboardStore } from "@/stores/dashboard";
import GoogleServices from "./GoogleServices.vue";
import GmailSidebar from "./GmailSidebar.vue";

const dashboardStore = useDashboardStore();
const isQuickMenuOpen = ref(false);
let uboardFrameLoaded = false;

const showQuickMenu = (): void => {
  isQuickMenuOpen.value = true;
};

const hideQuickMenu = (): void => {
  isQuickMenuOpen.value = false;
};

const loadUboardFrame = (): void => {
  if (uboardFrameLoaded) return;

  const iframe = document.querySelector(
    ".uboard-sidebar iframe",
  ) as HTMLIFrameElement | null;
  if (iframe && iframe.src === "about:blank") {
    iframe.src = iframe.dataset.src || "https://uboard.info/uec";
    uboardFrameLoaded = true;
  }
};
</script>

<style scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.brand-stack {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  position: relative;
  gap: 12px;
}

.logo {
  font-size: 24px;
  font-weight: 700;
  color: #1f73e6;
  letter-spacing: 0.5px;
  cursor: default;
}

.quick-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: white;
  border: 1px solid #e6e6e6;
  border-radius: 10px;
  padding: 12px;
  min-width: 180px;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 120;
}

.quick-menu-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #333;
}

.switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #d6d6d6;
  transition: 0.2s;
  border-radius: 999px;
}

.slider::before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.2s;
  border-radius: 50%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.switch input:checked + .slider {
  background-color: #1f73e6;
}

.switch input:checked + .slider::before {
  transform: translateX(16px);
}

.header-tools {
  display: flex;
  gap: 16px;
  align-items: center;
  flex: 0 0 auto;
}

.uboard-trigger-wrap,
.gmail-launcher {
  position: relative;
  display: flex;
  align-items: center;
}

.uboard-trigger,
.gmail-launcher-button {
  padding: 6px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: white;
  color: #5f6368;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.uboard-trigger:hover,
.gmail-launcher-button:hover {
  background: #f5f5f5;
  border-color: #d0d0d0;
}

.uboard-sidebar {
  position: absolute;
  top: 100%;
  right: 0;
  width: 400px;
  height: 500px;
  margin-top: 8px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 100;
}

.uboard-sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 12px;
  font-weight: 600;
}

.uboard-sidebar-header a {
  color: #1f73e6;
  text-decoration: none;
  font-size: 11px;
}

.uboard-sidebar-header a:hover {
  text-decoration: underline;
}

.uboard-sidebar iframe {
  flex: 1;
  border: none;
  border-radius: 0 0 8px 8px;
}
</style>
